import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Divider,
  Tooltip,
  Image,
  Space,
  Collapse,
  Panel,
  notification,
} from 'antd';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { ORDERS_TYPES, ORDER_STATUSES } from 'helpers/constants';
import moment from 'moment';
import { getOrdersBySlotIdForRouting } from 'store/requests/routing';

const Orders = ({
  orders,
  selectedSlot,
  setSelectedOrders,
  showPastOrders,
  zoneId,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState(orders);
  const [selectedOrderCount, setSelectedOrderCount] = useState(0);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setFilteredData(orders);
    setTotalOrderCount(orders.length);
  }, [orders]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    const filtered = orders.filter((record) =>
      Object.keys(record).some((key) =>
        record[key]?.toString()?.toLowerCase()?.includes(value?.toLowerCase()),
      ),
    );
    setFilteredData(filtered);
    setTotalOrderCount(filtered.length);
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedOrderCount(selectedRowKeys.length);
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
    setSelectedOrders(selectedRows);
  };

  const rowSelection = {
    selectedRows,
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const orderStatusFilterHandler = Object.keys(ORDER_STATUSES).map((order) => {
    return { text: t(order), value: order };
  });

  const reload = () => {
    if (selectedSlot.length > 0 ) {
      setTableLoading(true);
      getOrdersBySlotIdForRouting({
        slotIdList: [...selectedSlot],
        previousDayNotRoutedOrdersIncluded: false,
        zoneId,
      })
        .then((orders) => {
          setFilteredData(orders);
          setTotalOrderCount(orders.length);
          setTableLoading(false);
        })
        .finally(() => {
          setTableLoading(false);
        });
    } else {
      notification.error({ message: t('slotNotFound') });
    }
  };

  const columns = [
    {
      title: t('selectAll'),
      dataIndex: 'customerOrderNumber',
      key: 'customerOrderNumber',
      width: '70px',
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '',
      dataIndex: 'addressText',
      key: 'address',
      width: '120px',
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('orderStatus'),
      dataIndex: 'icons',
      width: '120px',
      filters: orderStatusFilterHandler,
      onFilter: (value, record) => record.orderStatus?.startsWith(value),
      filterSearch: true,
      render: (text, record) => (
        <Space>
          {record.containsFrozenProduct && (
            <Tooltip title={t('containsFrozen')}>
              <Image
                src="/static/img/frozenProduct.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.customerType === 'VIP' && (
            <Tooltip title={t('eliteCustomer')}>
              <Image
                src="/static/img/eliteCustomer.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'COLLECTED' && (
            <Tooltip title={t('collected')}>
              <Image
                src="/static/img/collected.svg"
                width={25}
                height={25}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'COLLECTING' && (
            <Tooltip title={t('collecting')}>
              <Image
                src="/static/img/gathering.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderStatus === 'NEW_ORDER' && (
            <Tooltip title={t('notCollected')}>
              <Image
                src="/static/img/notCollected.svg"
                width={25}
                height={25}
                preview={false}
              />
            </Tooltip>
          )}
          {record.partner === 'SOK' && (
            <Tooltip title="Sipariş Platformu: (CepteŞok)">
              <Image
                src="/static/img/ceptesoklogo.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.partner === 'ISTEGELSIN' && (
            <Tooltip title="Sipariş Platformu: (İsteGelsin)">
              <Image
                src="/static/img/istegelsinlogo.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
          {record.orderType === ORDERS_TYPES.RETURN && (
            <Tooltip title={t('returnOrder')}>
              <Image
                src="/static/img/returnOrder.svg"
                width={20}
                height={20}
                preview={false}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Divider style={{ margin: '12px 0px' }} orientation="left" plain>
        Güncel Siparişler
      </Divider>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        <Input
          placeholder={t('orderSearch')}
          onChange={handleSearch}
          style={{ width: '30%', borderRadius: '5px' }}
          suffix={<SearchOutlined />}
        />
        <span style={{ fontSize: '12px', marginLeft: '5px' }}>
          {t('selectedOrderCount')} : {selectedOrderCount}
        </span>
        <span style={{ fontSize: '12px', margin: '2px' }}>
          {t('totalOrderCountForRouting')} : {totalOrderCount}
        </span>
        <ReloadOutlined
          style={{ cursor: 'pointer' }}
          color="green"
          onClick={reload}
        />
      </div>
      <Divider style={{ margin: '12px 0px' }} />
      {orders && (
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          showHeader={true}
          rowSelection={rowSelection}
          rowKey="id"
          size="small"
          loading={tableLoading}
          scroll={{ y: showPastOrders ? '19vh' : '50vh' }}
        />
      )}
    </div>
  );
};

export default Orders;
